// src/app/pages/cost-metrics/simulation-panel/simulation-panel.ts
import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { KnobModule } from 'primeng/knob';
import { BadgeModule } from 'primeng/badge';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { HttpClient } from '@angular/common/http';

import { SimulationOverviewComponent } from './simulation-overview/simulation-overview';
import { CostingDataComponent } from './costing-data/costing-data';
import { SimulationControlsComponent } from './simulation-controls/simulation-controls';

import { LocationSelectionService } from '../service/location-selection.service';
import { AnalysisStore } from '../../core/shared/analysis.store';
import { SimulationApiService } from '../../core/shared/simulation-api.service';
import { CostingDataStore, CostGroupKey } from '../../core/shared/costing-data.store';
import { environment } from '../../../environments/environment';
import { YearStore } from '../../core/shared/year.store';
import { InterventionsSnapshot, InterventionStore } from '../../core/shared/intervention.store';
import { LoadingService } from '../../core/shared/loading.service';

// Updated import: single-year snapshot and full all-years series
import {
  CostMetricsSnapshot,
  CostMetricsSeries,
  buildCostMetricsSnapshotFromResp,
  buildCostMetricsSeriesFromResp
} from '../../core/shared/cost-metrics.snapshot';

type SimTab = 'simPanel' | 'simOverview' | 'costingData';

type SimItem = {
  id: string;
  title: string;
  createdAt: number;
  payload?: any[];
  color?: string;
  editing?: boolean;
  __expanded?: boolean;
  __protected?: boolean;
  interventionsSnapshot?: InterventionsSnapshot;
  rows?: any[] | null;
  htn?: Record<string, Record<string, number>> | null;

  // Single-year view (kept for quick UI needs)
  costMetrics?: CostMetricsSnapshot | null;

  // All-years, HTN-like series for selected cost metrics and KPIs
  costMetricsAllYears?: CostMetricsSeries | null;
};

interface UiRoot { version: number; countries: UiCountry[]; }
interface UiCountry { country: string; city: string; flag?: string; categories: UiCategory[]; }
interface UiCategory { category: string; interventions: UiIntervention[]; }
interface UiIntervention { id: number; name: string; switchKey?: string; parameters: UiParameter[]; }
interface UiParameter {
  key: string; interface_name: string; model_name: string;
  type: 'integer' | 'float' | 'fraction' | 'year';
  default: number; range: { min: number; max: number }; step: number; unit: string | null;
}

function isBaselineTitle(title: string | null | undefined): boolean {
  const t = (title || '').trim().toLowerCase();
  return t === 'simulation 1' || t === 'basic simulation';
}

@Component({
  selector: 'app-simulation-panel',
  standalone: true,
  templateUrl: './simulation-panel.html',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ChartModule,
    TabsModule,
    CheckboxModule,
    SelectModule,
    SliderModule,
    TooltipModule,
    AccordionModule,
    KnobModule,
    BadgeModule,
    ToastModule,
    DialogModule,
    SimulationOverviewComponent,
    CostingDataComponent,
    SimulationControlsComponent
  ],
  providers: [MessageService]
})
export class SimulationPanelComponent {
  private http = inject(HttpClient);
  private locationSel = inject(LocationSelectionService);
  private messageService = inject(MessageService);
  private analysisStore = inject(AnalysisStore);
  private simApi = inject(SimulationApiService);
  private costingStore = inject(CostingDataStore);
  private interventionStore = inject(InterventionStore);
  private yearStore = inject(YearStore);

  private readonly SELECTED_RUN_STORAGE_KEY = 'selected_run_id';
  private readonly STORAGE_KEY = 'simulations_list';
  private loader = inject(LoadingService);



  // Tabs
  isLoading = true;
  activeTab: SimTab = 'simPanel';
  isSimBlockExpanded = false;

  @ViewChild(SimulationControlsComponent) controls!: SimulationControlsComponent;

  // Selection context
selectedCountry = 'Brazil';
selectedCity: string | null = null;

  // Interventions root
  data: UiRoot | null = null;

  // Per-country UI state
  selectedOptions: Record<string, string[]> = {};
  openPanels: Record<string, number | null> = {};
  parameterValues: Record<number, number[]> = {};

  // Simulations
  simulations: SimItem[] = [];
  selectedSimulationIds = new Set<string>();
  private nextSimNumber = 1;
  private relativeTimer: any = null;

  private readonly titleColors = ['#4f46e5', '#ef4444', '#10b981', '#f59e0b', '#3b82f6', '#06b6d4', '#a855f7'];

  // Dialog state
  simNameDialogVisible = false;
  simNameInput = '';
  simDialogMode: 'add' | 'edit' = 'add';
  private simToEdit: { id: string; title: string } | null = null;

  // Payload dialog
  payloadDialogVisible = false;
  payloadDialogTitle = '';
  payloadDialogRunId: string | null = null;
  dialogScalarPairs: Array<{ key: string; val: string }> = [];
  dialogParamGroups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];
  dialogCostGroups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];

  // API URL for update
  private updateApiUrl = (environment as any)?.apiUpdateUrl || '/api/update';

  // Rehydrate stored runs
  private rehydrateAnalysisStoreFromPersisted(): void {
    const sims = this.simulations || [];
    for (const sim of sims) {
      const id = String(sim.id);
      const title = sim.title || '';
      const color = sim.color || '#64748b';

      let rows = Array.isArray(sim.rows) ? sim.rows : [];
      let htn = sim.htn ?? null;

      if ((!rows || rows.length === 0) && sim.payload?.[0]?.request) {
        try {
          const req = sim.payload[0].request;
          const mapped = this.simApi.mapToRows(req);
          rows = Array.isArray(mapped?.rows) ? mapped.rows : [];
          htn = req?.htn ?? htn;
        } catch { }
      }

      const exists = this.analysisStore.getRunsSnapshot().some(r => r.id === id);
      if (!exists && rows.length) {
        this.analysisStore.addRun(id, title, color, rows, htn || null);
      }
    }
  }

  ngOnInit(): void {
    const loc = this.locationSel.getSelected();
if (loc?.country) {
  this.selectedCountry = loc.country;
}
if (loc?.city) {
  this.selectedCity = loc.city;
}
    this.yearStore.setDisplayMinYear(2019);
    // 1) Load simulations list from localStorage
    this.loadPersistedSimulations();

    // 2) Rehydrate AnalysisStore runs
    this.rehydrateAnalysisStoreFromPersisted();

    // 3) Preload Basic Simulation if missing
    const hasSim1 = this.simulations.some(s => (s.title || '').trim().toLowerCase() === 'simulation 1');
    if (!hasSim1) {
      this.simApi.loadInitialFromFile('assets/initial-response.json').subscribe({
        next: (resp) => {
          const mapped = this.simApi.mapToRows(resp);
          const rowsArr = Array.isArray(mapped?.rows) ? mapped.rows : [];
          const id = (resp as any)?.run_id || 'initial-run';
          const title = 'Basic Simulation';
          const color = this.titleColors[0];

          const runs = this.analysisStore.getRunsSnapshot();
          const existingBaseline = runs.find(r => (r.title || '').trim().toLowerCase() === 'basic simulation' || r.id === String(id));

          let baselineRunId = String(id);
          if (!existingBaseline) {
            this.analysisStore.addRun(baselineRunId, title, color, rowsArr, (resp as any)?.htn);
          } else {
            baselineRunId = existingBaseline.id;
          }

          // Persist baseline cost metrics (cm)
          const baseCostCm = (resp as any)?.cm ?? null;
          if (baseCostCm) {
            this.analysisStore.setRunCostCm(baselineRunId, baseCostCm);
            const respWithId = { ...(resp as any), run_id: baselineRunId };
            this.analysisStore.upsertRunCostCmFromApiResponse(respWithId);
          }

          // Add to local list once, with cost metrics snapshots (single-year + all-years)
          // Inside ngOnInit(), in the block where you push the baseline sim:
          if (!this.simulations.some(s => String(s.id) === String(baselineRunId))) {
            const uiYear = (this.yearStore as any)?.getYear?.();
            const cmSnap = buildCostMetricsSnapshotFromResp(resp, { uiYear });
            const cmSeries = buildCostMetricsSeriesFromResp(resp);

            // Build a request identical to new simulations AND include HTN
            const basePayload = this.buildNewPayload();
            const baselineRequest = {
              ...basePayload,
              htn: (resp as any)?.htn ?? null,      // ADD THIS
              simulation_run_name: title,           // "Basic Simulation"
              run_id: baselineRunId,
              session_id: this.getSessionId(),
              user_id: this.getUserId(),
              City: this.getCity(),
              Country: basePayload.Country ?? 'Brazil'
            };

            this.simulations = [
              ...this.simulations,
              {
                id: baselineRunId,
                title,
                createdAt: Date.now(),
                payload: [{ preloaded: true, request: baselineRequest, response: resp, run_id: baselineRunId }], color,
                __expanded: false,
                __protected: true,
                rows: rowsArr,
                htn: (resp as any)?.htn ?? null,
                costMetrics: cmSnap,
                costMetricsAllYears: cmSeries
              }
            ];
          }

          this.persistSimulations();
          this.nextSimNumber = Math.max(this.nextSimNumber, 2);
          this.selectRunGlobally(baselineRunId);
          this.yearStore.setYear(2045);              
        },
        error: (err) => console.error('Failed to load initial-response.json', err)
      });
    }

    // 4) Restore selection after runs exist
    this.analysisStore.runsList$.subscribe(runs => {
      if (!runs.length) return;
      this.restoreSelectionSafely();
    });

    // Periodic UI refresh (optional)
    this.relativeTimer = setInterval(() => (this.simulations = [...this.simulations]), 30000);
    setTimeout(() => (this.isLoading = false), 1500);
  }

  ngOnDestroy(): void {
    if (this.relativeTimer) clearInterval(this.relativeTimer);
  }

onTabChange(val: string | number | undefined) {
  // PrimeNG Tabs can emit undefined; keep current tab or fall back to simPanel
  const next = (val ?? this.activeTab) as string;
  this.activeTab = (next || 'simPanel') as SimTab;
}

  onOverviewChartReady() { }

  private restoreSelectionSafely(): void {
    try {
      const saved = localStorage.getItem(this.SELECTED_RUN_STORAGE_KEY);
      const runs = this.analysisStore.getRunsSnapshot();
      const currentSel = this.analysisStore.getSelectedRunId();

      // Keep existing valid selection
      if (currentSel && runs.some(r => r.id === currentSel)) return;

      if (saved && runs.some(r => r.id === saved)) {
        this.analysisStore.selectRun(saved);
      } else if (runs.length) {
        const latestId = runs[runs.length - 1].id;
        this.selectRunGlobally(latestId);
      }
    } catch { }
  }

  // Centralized selection helper: store + localStorage
  private selectRunGlobally(runId: string) {
    this.analysisStore.selectRun(runId);
    try { localStorage.setItem(this.SELECTED_RUN_STORAGE_KEY, runId); } catch { }
  }

  // Title click: select simulation and (optionally) jump year
  onSimulationTitleClick(sim: { id: string | number }) {
    const id = String(sim.id);
    this.selectRunGlobally(id);
    this.yearStore.setYear(2045);
  }

  // Build payloads
  private ensureSelectedCountryExists(): void {
    const root = this.data;
    if (!root || !root.countries?.length) return;
    const sc = this.selectedCountry;
    if (!root.countries.some(c => c.country === sc)) {
      this.selectedCountry = root.countries[0].country;
      this.persistMinimalSelection();
    }
  }
  onCountryChange(): void {
    this.persistMinimalSelection();
    this.resetPerCountryState();
  }
  onCityChange(city: string | null): void {
  this.selectedCity = city;
  this.persistMinimalSelection();
}
  private persistMinimalSelection(): void {
    const country = this.selectedCountry;
    const city = this.selectedCity ?? '';
    this.locationSel.setSelected({ country, city });
  }
  private currentCountry(): UiCountry | null {
    const root = this.data;
    const sc = this.selectedCountry;
    if (!root) return null;
    return root.countries.find(c => c.country === sc) ?? null;
  }
  resetPerCountryState(): void {
    this.selectedOptions = {};
    this.initializeSelectedOptions();
    this.openPanels = {};
    this.initializeOpenPanels();
    this.parameterValues = {};
    this.initializeParameterValues();
  }
  private initializeSelectedOptions(): void {
    const cc = this.currentCountry();
    if (!cc) return;
    cc.categories.forEach(cat => (this.selectedOptions[cat.category] ??= []));
  }
  private initializeOpenPanels(): void {
    const cc = this.currentCountry();
    if (!cc) return;
    cc.categories.forEach(cat => {
      if (!(cat.category in this.openPanels)) this.openPanels[cat.category] = null;
    });
  }
  private initializeParameterValues(): void {
    const cc = this.currentCountry();
    if (!cc) return;
    cc.categories.forEach(cat => {
      cat.interventions.forEach(intv => {
        if (!this.parameterValues[intv.id]) {
          this.parameterValues[intv.id] = intv.parameters.map(p => p.default);
        }
      });
    });
  }

  private hashPayload(payload: any): string {
    try {
      const stableStringify = (obj: any): string => {
        if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
        if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
        const keys = Object.keys(obj).sort();
        return `{${keys.map(k => `"${k}":${stableStringify(obj[k])}`).join(',')}}`;
      };
      return stableStringify(payload);
    } catch {
      return JSON.stringify(payload);
    }
  }

  buildNewPayload(): any {
    const cc = this.currentCountry();
    const country = this.selectedCountry ?? null;
    const city = this.selectedCity ?? null;

    const selectedByCategory = this.selectedOptions;
    const isIntvEnabled = (name: string, category: string) =>
      (selectedByCategory[category] ?? []).includes(name);
    const getParamVal = (intv: any, pIdx: number, fallback: number) =>
      this.parameterValues[intv.id]?.[pIdx] ?? fallback;

    const parameters: any = {
      switch1: 1, switch2: 1, switch3: 1, switch4: 1, switch5: 1,
      switch6: 1, switch7: 1, switch8: 1, switch9: 1, switch10: 1,
      'switch learning': 0,
      'switch diagnosis data or intervention': 1,
      'switch treatment data or intervention': 1,
      'switch prehypertension treatment': 1
    };

    const mapIntervention = (category: string, intv: any) => {
      const name: string = intv.name;
      const enabled = isIntvEnabled(name, category);
      const groupObj: any = {};
      const set = (k: string, v: number | null) => (groupObj[k] = v);
      const val = (idx: number) => getParamVal(intv, idx, intv.parameters[idx].default);

      switch (name) {
        case 'Awareness raising':
          set('switch campaigns', enabled ? 1 : null);
          set('number of campaigns per year', val(0));
          set('number of people reached per campaign', val(1));
          set('fraction of aware from campaigns', val(2));
          set('start time campaign 2019', val(3));
          set('years funded campaign', val(4));
          parameters['Awareness raising'] = groupObj; break;
        case 'Screening corner':
          set('switch screening corner', enabled ? 1 : null);
          set('desired number of screening corner', val(0));
          set('desired screening corner productivity', val(1));
          set('start time building screening corner 2018', val(2));
          set('time to build corner', val(3));
          parameters['Screening corner'] = groupObj; break;
        case 'Active search':
          set('switch active screen', enabled ? 1 : null);
          set('desired active search productivity', val(0));
          set('start time building active screen 2018', val(1));
          set('time to build active screen productivity', val(2));
          parameters['Active search'] = groupObj; break;
        case 'Diagnostic capacity UBS':
          set('switch UBS diagnostic productivity', enabled ? 1 : null);
          set('desired additional UBS diagnostic productivity', val(0));
          set('percentage of max capacity from initial capacity diagnosis', val(1));
          set('start time building UBS diagnostic productivity', val(2));
          set('time to build UBS diagnostic productivity', val(3));
          parameters['Diagnostic capacity UBS'] = groupObj; break;
        case 'Treatment capacity UBS':
          set('switch UBS HTN treatment productivity', enabled ? 1 : null);
          set('desired UBS HTN treatment productivity', val(0));
          set('percentage of max capacity from initial capacity treatment', val(1));
          set('start time building UBS HTN treatment productivity', val(2));
          set('time to build HTN treatment productivity', val(3));
          parameters['Treatment capacity UBS'] = groupObj; break;
        case 'Caring for all protocol':
          set('switch training caring for all protocol', enabled ? 1 : null);
          set('goal effect training caring for all protocol % on control rate', val(0));
          set('goal effect training caring for all protocol % on diagnostic sensitivity', val(1));
          set('start time training caring for all protocol', val(2));
          set('time to reach goal tcap', val(3));
          parameters['Caring for all protocol'] = groupObj; break;
        case 'Remote training':
          set('switch remote training', enabled ? 1 : null);
          set('goal effect remote training % on control rate', val(0));
          set('goal effect remote training % on diagnostic sensitivity', val(1));
          set('start time remote training', val(2));
          set('time to reach goal rt', val(3));
          parameters['Remote training'] = groupObj; break;
        case 'Risk stratification tool':
          set('switch risk stratification tool', enabled ? 1 : null);
          set('goal effect risk stratification tool % on control rate', val(0));
          set('goal effect risk stratification tool % on diagnostic sensitivity', val(1));
          set('start time risk stratification tool', val(2));
          set('time to reach goal rst', val(3));
          parameters['Risk stratification tool'] = groupObj; break;
        case 'PAP card':
          set('switch PAP card', enabled ? 1 : null);
          set('goal effect PAP card % on cease treatment', val(0));
          set('goal effect PAP card % on resume treatment', val(1));
          set('start time PAP card 2024', val(2));
          set('time to reach goal pc', val(3));
          parameters['PAP card'] = groupObj; break;
        case 'Adherence combo':
          set('switch adherence combo', enabled ? 1 : null);
          set('goal effect adherence combo % on cease treatment', val(0));
          set('goal effect adherence combo % on resume treatment', val(1));
          set('start time adherence combo 2024', val(2));
          set('time to reach goal ac', val(3));
          parameters['Adherence combo'] = groupObj; break;
        case 'Governance model':
          set('switch management and governance tool', enabled ? 1 : null);
          set('goal effect management and governance model % on responsiveness', val(0));
          set('start time management and governance model', val(1));
          set('time to reach goal mgt', val(2));
          parameters['Governance model'] = groupObj; break;
        case 'Management panel':
          set('switch management panel', enabled ? 1 : null);
          set('goal effect management panel % on responsiveness', val(0));
          set('start time management panel', val(1));
          set('time to reach goal mp', val(2));
          parameters['Management panel'] = groupObj; break;
      }
    };
    cc?.categories.forEach(cat => cat.interventions.forEach(intv => mapIntervention(cat.category, intv)));

    const COST_DEFAULTS: Record<string, Record<string, number>> = {
      'Health Sector Cost': { 'Discount rate': 0.03, 'Diagnosis cost': 38, 'Annual hypertension treatment cost': 199, 'Annual CVD treatment cost': 200, 'Stroke event cost': 5864, 'CHD event cost': 1100 },
      'Awareness raising': { 'Development of the curriculum': 500, 'Printing training and advocacy materials': 500, 'Salary and transport': 300, 'Training heart friends and ambassadors': 500, 'Communication cost': 200 },
      'Screening Corner': { 'Screening corner equipment': 485, 'Advocacy materials, production and delivery': 109, 'Health facility personnel': 3258, 'Maintenance cost for SC': 100 },
      'Active search': { 'BP measuring device': 997, 'Salaries per outreach activities': 102, 'Transport for outreach activity': 5, 'Communication cost': 200 },
      'Caring for all protocol': { 'Development of the protocol': 5000, 'Printing training and advocacy materials protocol': 11, 'Training caring for all protocol for health staff': 88, 'Refresher training every year': 118, 'Communication cost': 200 },
      'Remote training': { 'Development of the curriculum remote training': 5000, 'Printing training and advocacy materials training': 50, 'Refresher training every remote training': 118, 'Communication cost': 200 },
      'Risk stratification tool': { 'Development of the risk stratification tool': 5000, 'Printing training and advocacy material tool': 50, 'Refresher training city risk stratification tool': 118, 'Printing material tool': 44, 'Communication cost': 200 },
      'PAP card': { 'Development of the PAP card': 5000, 'Initial training to health facilities personnel': 48, 'Printing and production of all materials': 11, 'Communication cost': 200, 'Further printing of materials': 44 },
      'Adherence combo': { 'Development of the adherence combo package': 5000, 'Printing training and advocacy material combo': 80, 'Training adherence combo to health proffesionals': 133, 'Refresher training every year combo': 118, 'Further printing of adherence forms': 44, 'Communication cost': 200 },
      'Governance model': { 'Salary for governance role': 118 },
      'Management panel': { 'Salary manegerial panel utilisation': 527 }
    };
    const editedCosts: Partial<Record<CostGroupKey, Record<string, number>>> = this.costingStore?.getValues?.() ?? {};
    const mergeGroup = (groupName: CostGroupKey) => {
      const def = COST_DEFAULTS[groupName] || {};
      const cur = editedCosts[groupName] || {};
      const out: Record<string, number> = {};
      Object.keys(def).forEach(k => (out[k] = def[k]));
      Object.keys(cur).forEach(k => (out[k] = cur[k]));
      return out;
    };
    const Costmetrics = {
      'Health Sector Cost': mergeGroup('Health Sector Cost'),
      'Awareness raising': mergeGroup('Awareness raising'),
      'Screening Corner': mergeGroup('Screening Corner'),
      'Active search': mergeGroup('Active search'),
      'Caring for all protocol': mergeGroup('Caring for all protocol'),
      'Remote training': mergeGroup('Remote training'),
      'Risk stratification tool': mergeGroup('Risk stratification tool'),
      'PAP card': mergeGroup('PAP card'),
      'Adherence combo': mergeGroup('Adherence combo'),
      'Governance model': mergeGroup('Governance model'),
      'Management panel': mergeGroup('Management panel')
    };

    return { City: city, Country: country, parameters, Costmetrics };
  }

  // Resolve cm from various response shapes for storing in AnalysisStore
  private getExplicitCostCm(resp: unknown): Record<string, Record<string, number>> | null {
    const r = resp as any;
    return (
      r?.cm ??
      r?.payload?.[0]?.response?.cm ??
      r?.payload?.[0]?.response?.costCm ??
      r?.costCm ??
      null
    );
  }

  addSimulation(): void {
    if (this.simulations.length >= 5) {
      this.loader.hide();
      this.messageService.add({ severity: 'error', summary: 'Limit reached', detail: 'You can add a maximum of 5 simulations.', life: 2500 });
      return;
    }

    const basePayload = this.controls?.buildNewPayload?.();
    if (!basePayload) {
      this.loader.hide();
      this.messageService.add({ severity: 'error', summary: 'Payload error', detail: 'Failed to build payload. Open Simulation Controls and adjust inputs.', life: 2000 });
      return;
    }

    let name: string = (this.simNameInput || '').trim();
    if (!name || this.isDuplicateName(name)) {
      name = this.suggestSimulationName();
    }

    const enriched = {
      ...basePayload,
      session_id: this.getSessionId(),
      user_id: this.getUserId(),
      simulation_run_name: name,
      City: this.getCity(),
      Country: basePayload.Country ?? 'Brazil'
    };

    if (this.isDuplicateCoreParams(enriched)) {
      this.loader.hide();
      this.messageService.add({
        severity: 'error',
        summary: 'Duplicate simulation',
        detail: 'A simulation with identical intervention and cost metrics already exists. Adjust inputs before adding.',
        life: 2500
      });
      return;
    }

    this.simApi.startRun(enriched).subscribe({
      next: (resp) => {
        this.loader.hide();
        const mapped = this.simApi.mapToRows(resp);
        const rowsArr = Array.isArray(mapped?.rows) ? mapped.rows : [];

        let runId = (resp as any)?.run_id || (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
        const existingIds = new Set(this.simulations.map(s => String(s.id)));
        if (existingIds.has(String(runId))) {
          runId = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
        }

        const color = this.titleColors[(this.nextSimNumber - 1) % this.titleColors.length];

        // Create the run in the store with the final runId
        this.analysisStore.addRun(runId, name, color, rowsArr, (resp as any)?.htn);
        this.nextSimNumber++;

        // Save cm in store if present
        const explicitCostCm = this.getExplicitCostCm(resp);
        if (explicitCostCm) {
          this.analysisStore.setRunCostCm(String(runId), explicitCostCm);
        }
        const respWithRunId = { ...(resp as any), run_id: String(runId) };
        this.analysisStore.upsertRunCostCmFromApiResponse(respWithRunId);

        // Interventions snapshot (include activeCount; persist)
        const rawSnap = this.controls.buildSnapshot();
        const activeCount = Object.values(rawSnap.selectedOptions || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
        const fullSnap: InterventionsSnapshot = { ...rawSnap, activeCount } as InterventionsSnapshot;
        this.interventionStore.setSnapshot(String(runId), fullSnap);

        // Build Cost Metrics snapshot (single-year) and all-years series for local storage
        const uiYear = (this.yearStore as any)?.getYear?.();
        const cmSnap = buildCostMetricsSnapshotFromResp(resp, { request: enriched, uiYear });
        const cmSeries = buildCostMetricsSeriesFromResp(resp, { request: enriched });

        const requestWithHtn = { ...enriched, htn: (resp as any)?.htn ?? null }; // ADD THIS

        this.simulations = [
          ...this.simulations,
          {
            id: String(runId),
            title: name,
            createdAt: Date.now(),
            payload: [{ request: requestWithHtn, run_id: String(runId) }],       // USE requestWithHtn
            color,
            __expanded: false,
            interventionsSnapshot: fullSnap,
            rows: rowsArr,
            htn: (resp as any)?.htn ?? null,
            costMetrics: cmSnap,
            costMetricsAllYears: cmSeries
          }
        ];
        this.persistSimulations();

        // Select and fix year
        this.selectRunGlobally(String(runId));
        this.yearStore.setYear(2045);
      },
      error: (err) => {
        this.loader.hide();
        this.messageService.add({ severity: 'error', summary: 'Run error', detail: 'Could not start simulation run.', life: 2500 });
        console.error('startRun error:', err);
      }
    });
  }

  // Edit dialog
  openSimNameDialogForAdd() {
    this.simDialogMode = 'add';
    this.simToEdit = null;
    this.simNameInput = this.suggestSimulationName();
    this.simNameDialogVisible = true;
  }
  onEditClick(sim: { id: string | number; title: string }) {
    this.openSimNameDialogForEdit({ id: String(sim.id), title: sim.title });
    this.simNameInput = sim.title;
    this.simNameDialogVisible = true;
  }
  openSimNameDialogForEdit(sim: { id: string | number; title: string }) {
    this.simDialogMode = 'edit';
    this.simToEdit = { id: String(sim.id), title: sim.title };
  }
  onSimNameSave() {
    const name: string = (this.simNameInput || '').trim();
    if (!name) { this.simNameDialogVisible = false; return; }

    if (this.simDialogMode === 'add') {
      this.simNameDialogVisible = false;
      this.loader.show('Adding Simulation...');
      this.addSimulation();
      return;
    }

    if (this.isDuplicateName(name)) {
      this.loader.hide();
      this.messageService.add({ severity: 'error', summary: 'Duplicate name', detail: 'Simulation name already exists. Choose a different name.', life: 2500 });
      return;
    }

    if (this.simToEdit) {
      const id = String(this.simToEdit.id);
      const sim = this.simulations.find(s => String(s.id) === id);
      if (!sim) {
        this.messageService.add({ severity: 'error', summary: 'Not found', detail: 'Simulation not found.', life: 2000 });
        return;
      }

      const payloadRename = {
        action: 'rename',
        session_id: this.getSessionId(),
        user_id: this.getUserId(),
        city: this.getCity(),
        old_simulation_run_name: sim.title,
        new_simulation_run_name: name
      };
      this.http.post(this.updateApiUrl, payloadRename).subscribe({
        next: () => {
          this.simulations = this.simulations.map(s => String(s.id) === id ? { ...s, title: name, editing: false } : s);
          this.analysisStore.renameRun(id, name);
          this.persistSimulations();
          this.messageService.add({ severity: 'success', summary: 'Title updated', detail: 'Simulation title saved.', life: 1200 });
          this.simNameDialogVisible = false;
        },
        error: () => {
          this.loader.hide();
          this.messageService.add({ severity: 'error', summary: 'Rename failed', detail: 'Could not update simulation name.', life: 2000 });
        }
      });
    }
  }

  // Delete single
  deleteSimulation(id: string): void {
    const sim = this.simulations.find(s => String(s.id) === String(id));
    if (!sim) return;

    const isBaseline = isBaselineTitle(sim.title) || sim.__protected === true;
    if (isBaseline) {
      this.messageService.add({ severity: 'warn', summary: 'Not allowed', detail: 'Basic Simulation cannot be deleted.', life: 2000 });
      return;
    }

    if (this.simulations.length <= 1) {
      this.messageService.add({ severity: 'error', summary: 'Cannot delete', detail: 'At least one simulation must remain.', life: 2000 });
      return;
    }

    const payloadDelete = {
      action: 'delete',
      session_id: this.getSessionId?.(),
      user_id: this.getUserId?.(),
      city: this.getCity?.(),
      old_simulation_run_name: sim.title
    };
    this.loader.show('Deleting Simulation...');
    this.http.post(this.updateApiUrl, payloadDelete).subscribe({
      next: () => {
        this.loader.hide();
        this.simulations = this.simulations.filter(s => String(s.id) !== String(id));
        this.selectedSimulationIds.delete(String(id));
        this.analysisStore.removeRun(String(id));
        this.interventionStore.removeSnapshot(String(id));

        const runs = this.analysisStore.getRunsSnapshot();
        const nextId = runs.length ? runs[runs.length - 1].id : '';
        if (nextId) this.selectRunGlobally(nextId);
        else try { localStorage.removeItem(this.SELECTED_RUN_STORAGE_KEY); } catch { }

        this.nextSimNumber = Math.max(this.simulations.length + 1, this.nextSimNumber);
        this.persistSimulations?.();

        this.messageService.add({ severity: 'info', summary: 'Deleted', detail: `${sim.title} removed.`, life: 1500 });
      },
      error: (err) => {
        this.loader.hide();
        console.error('Delete backend failed:', err);
        this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: 'Unable to delete simulation. Please try again.', life: 2000 });
      }
    });
  }

  // Delete selected
  deleteSelected(): void {
    if (this.selectedSimulationIds.size === 0) return;

    const deletableIds = Array.from(this.selectedSimulationIds).filter(id => {
      const sim = this.simulations.find(s => String(s.id) === String(id));
      return sim && !(sim.__protected || (sim.title || '').trim().toLowerCase() === 'basic simulation');
    });

    if (deletableIds.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Not allowed', detail: 'Basic Simulation cannot be deleted.', life: 2000 });
      return;
    }

    const remaining = this.simulations.length - deletableIds.length;
    if (remaining < 1) {
      this.messageService.add({ severity: 'error', summary: 'Cannot delete', detail: 'At least one simulation must remain.', life: 2000 });
      return;
    }

    const toDelete = deletableIds
      .map(id => this.simulations.find(s => String(s.id) === String(id)))
      .filter((s): s is SimItem => !!s);

    let completed = 0;
    let failCount = 0;

    this.loader.show('Deleting Simulation...');
    const afterAll = () => {
      this.loader.hide();
      const successIds = new Set(toDelete.filter(s => (s as any).__deletedSuccess === true).map(s => String(s.id)));

      if (successIds.size > 0) {
        this.simulations = this.simulations.filter(s => !successIds.has(String(s.id)));
        successIds.forEach(id => {
          this.analysisStore.removeRun(String(id));
          this.interventionStore.removeSnapshot(String(id));
        });
        this.persistSimulations?.();
      }

      this.selectedSimulationIds.clear();

      const runs = this.analysisStore.getRunsSnapshot();
      const nextId = runs.length ? runs[runs.length - 1].id : '';
      if (nextId) this.selectRunGlobally(nextId);
      else try { localStorage.removeItem(this.SELECTED_RUN_STORAGE_KEY); } catch { }

      if (successIds.size > 0) {
        this.messageService.add({ severity: 'info', summary: 'Deleted', detail: `${successIds.size} simulation(s) removed.`, life: 1500 });
      }
      if (failCount > 0) {
        this.messageService.add({ severity: 'warn', summary: 'Some failed', detail: `${failCount} simulation(s) could not be deleted.`, life: 2000 });
      }
    };

    toDelete.forEach(sim => {
      const payloadDelete = {
        action: 'delete',
        session_id: this.getSessionId?.(),
        user_id: this.getUserId?.(),
        city: this.getCity?.(),
        old_simulation_run_name: sim.title
      };

      this.http.post(this.updateApiUrl, payloadDelete).subscribe({
        next: () => {
          (sim as any).__deletedSuccess = true;
          completed++;
          if (completed === toDelete.length) afterAll();
        },
        error: (err) => {
          console.error('Bulk delete backend failed for', sim.title, err);
          (sim as any).__deletedSuccess = false;
          failCount++;
          completed++;
          if (completed === toDelete.length) afterAll();
        }
      });
    });
  }

  saveEdit(item: any, newTitle: string): void {
    const t = (newTitle ?? '').trim();
    if (t) {
      item.title = t;
      this.analysisStore.renameRun(String(item.id), t);
      this.messageService.add({ severity: 'success', summary: 'Title updated', detail: 'Simulation title saved.', life: 1200 });
    }
    item.editing = false;
  }
  cancelEdit(item: any): void { item.editing = false; }

  toggleSelectAll(checked: boolean): void {
    if (checked) this.simulations.forEach(s => this.selectedSimulationIds.add(String(s.id)));
    else this.selectedSimulationIds.clear();
  }
  toggleSelection(id: string | number, checked: boolean): void {
    const key = String(id);
    if (checked) this.selectedSimulationIds.add(key);
    else this.selectedSimulationIds.delete(key);
  }
  isAllSelected(): boolean {
    return this.simulations.length > 0 && this.selectedSimulationIds.size === this.simulations.length;
  }

  relativeTime(ts: number): string {
    const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }

  trackById = (_: number, item: { id?: string | number }) => String(item?.id);

  // Nested payload view
  toggleDetails(sim: SimItem): void {
    sim.__expanded = !sim.__expanded;
  }
  private getSimRequest(sim: SimItem): any {
    return sim?.payload?.[0]?.request || {};
  }
  getScalarPairs(sim: any): Array<{ key: string; val: string }> {
    const req = this.getSimRequest(sim);
    const params = req.parameters || {};
    const pairs: Array<{ key: string; val: string }> = [];
    Object.keys(params).forEach(k => {
      const v = (params as any)[k];
      if (typeof v !== 'object') {
        pairs.push({ key: k, val: String(v) });
      }
    });
    return pairs;
  }
  getParamGroups(sim: any): Array<{ name: string; pairs: Array<{ key: string; val: string }> }> {
    const req = this.getSimRequest(sim);
    const params = req.parameters || {};
    const groups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];
    Object.keys(params).forEach(group => {
      const v = (params as any)[group];
      if (v && typeof v === 'object') {
        const pairs: Array<{ key: string; val: string }> = Object.keys(v).map(k => ({
          key: k, val: String((v as any)[k])
        }));
        groups.push({ name: group, pairs });
      }
    });
    return groups;
  }
  getCostGroups(sim: any): Array<{ name: string; pairs: Array<{ key: string; val: string }> }> {
    const req = this.getSimRequest(sim);
    const cm = req.Costmetrics || {};
    const groups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];
    Object.keys(cm).forEach(group => {
      const kv = (cm as any)[group] || {};
      const pairs: Array<{ key: string; val: string }> = Object.keys(kv).map(k => ({
        key: k, val: String(kv[k])
      }));
      groups.push({ name: group, pairs });
    });
    return groups;
  }

  openPayloadDialog(sim: SimItem): void {
    let req = this.getSimRequest(sim);
    this.payloadDialogTitle = sim.title || '';
    this.payloadDialogRunId = req?.run_id || null;
    console.log('[PayloadDialog] request.htn keys:', Object.keys(req?.htn ?? {})); // <-- TEMP CHECK


    // else fall back to local builder if controls is not available.
    const hasGroupParams =
      req?.parameters &&
      Object.keys(req.parameters).some(k => {
        const v = (req.parameters as any)[k];
        return typeof v === 'object' && v !== null;
      });

    if (!hasGroupParams) {
      const gen = this.controls?.buildNewPayload?.() || this.buildNewPayload();
      req = {
        ...req,
        parameters: gen?.parameters ?? {},
        Costmetrics: gen?.Costmetrics ?? {}
      };
    }

    const params = req?.parameters || {};

    // Skip global switches (already fixed)
    const skipScalar = new Set<string>([
      'switch1', 'switch2', 'switch3', 'switch4', 'switch5',
      'switch6', 'switch7', 'switch8', 'switch9', 'switch10',
      'switch learning',
      'switch diagnosis data or intervention',
      'switch treatment data or intervention',
      'switch prehypertension treatment'
    ]);

    // Scalars without global switches
    this.dialogScalarPairs = Object.keys(params)
      .filter(k => typeof (params as any)[k] !== 'object' && !skipScalar.has(k))
      .map(k => ({ key: k, val: String((params as any)[k]) }));

    // Intervention groups
    this.dialogParamGroups = Object.keys(params)
      .filter(k => typeof (params as any)[k] === 'object' && (params as any)[k] !== null)
      .map(group => {
        const v = (params as any)[group] || {};
        const pairs = Object.keys(v).map(k => ({ key: k, val: String(v[k]) }));
        return { name: group, pairs };
      });

    // Costmetrics (unchanged)
    const cm = req?.Costmetrics || {};
    this.dialogCostGroups = Object.keys(cm).map(group => {
      const kv = (cm as any)[group] || {};
      const pairs = Object.keys(kv).map(k => ({ key: k, val: String(kv[k]) }));
      return { name: group, pairs };
    });

    this.payloadDialogVisible = true;
  }

  downloadPayloadExcel(): void {
    type Row = { section: string; group: string; key: string; value: string };
    const rows: Row[] = [];
    this.dialogScalarPairs.forEach(p => rows.push({ section: 'Parameters', group: 'Global switches', key: p.key, value: p.val }));
    this.dialogParamGroups.forEach(g => g.pairs.forEach(kv => rows.push({ section: 'Parameters', group: g.name, key: kv.key, value: kv.val })));
    this.dialogCostGroups.forEach(g => g.pairs.forEach(kv => rows.push({ section: 'Costmetrics', group: g.name, key: kv.key, value: kv.val })));
    const headers = ['Section', 'Group', 'Key', 'Value'];
    const csvLines = [headers.join(','), ...rows.map(r => [r.section, r.group, r.key, r.value].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))];
    const csvContent = csvLines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = `${this.payloadDialogTitle || 'simulation'}-payload.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // Confirm delete dialog
  confirmDeleteVisible = false;
  confirmDeleteTarget: { id: string; title: string } | null = null;
  openDeleteConfirm(sim: { id: string | number; title: string }): void {
    this.confirmDeleteTarget = { id: String(sim.id), title: sim.title };
    this.confirmDeleteVisible = true;
  }
  confirmDeleteProceed(): void {
    if (!this.confirmDeleteTarget) return;
    const { id } = this.confirmDeleteTarget;
    this.confirmDeleteVisible = false;
    this.deleteSimulation(id);
  }
  confirmDeleteCancel(): void {
    this.confirmDeleteVisible = false;
    this.confirmDeleteTarget = null;
  }

  private persistSimulations() {
    try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.simulations)); } catch { }
  }

  private loadPersistedSimulations() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const sims: SimItem[] = JSON.parse(raw);
      if (!Array.isArray(sims)) return;

      // MIGRATION: patch simulations whose request.parameters lack intervention groups
      const patched = sims.map(s => {
        const req = s?.payload?.[0]?.request || null;
        const hasGroupParams =
          req?.parameters &&
          Object.keys(req.parameters).some(k => {
            const v = (req.parameters as any)[k];
            return typeof v === 'object' && v !== null;
          });

        if (!hasGroupParams) {
          const gen = this.controls?.buildNewPayload?.() || this.buildNewPayload();
          const newReq = {
            ...req,
            parameters: gen?.parameters ?? {},
            Costmetrics: gen?.Costmetrics ?? {}
          };
          const newPayload0 = { ...(s.payload?.[0] ?? {}), request: newReq };
          return { ...s, payload: [newPayload0, ...(s.payload?.slice(1) ?? [])] };
        }
        return s;
      });

      this.simulations = patched;
      // Persist the patched list once so future loads are clean
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.simulations));

      this.nextSimNumber = Math.max(
        (patched.length ? patched.length + 1 : 1),
        (patched.map(s => Number(String(s.title).replace(/^\D+/, '').trim()) || 0).reduce((a, b) => Math.max(a, b), 0) + 1)
      );

      // Rehydrate intervention snapshots if present
      for (const sim of this.simulations) {
        const snap = (sim as any)?.interventionsSnapshot;
        if (snap) this.interventionStore.setSnapshot(String(sim.id), snap);
      }
    } catch { }
  }

  // Session/user helpers
 private getCity(): string {
  const loc = this.locationSel?.selected?.();
  return loc?.city || this.selectedCity || '';
}
  private getSessionId(): string {
    const key = 'session_id';
    let sid = sessionStorage.getItem(key);
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(key, sid);
    }
    return sid;
  }
  private getUserId(): string {
    try {
      // Prefer email (usually unique), then username; fall back to localStorage if needed
      const email =
        sessionStorage.getItem('userEmail') ||
        localStorage.getItem('userEmail') ||
        '';

      const name =
        sessionStorage.getItem('userName') ||
        sessionStorage.getItem('username') ||
        localStorage.getItem('userName') ||
        localStorage.getItem('username') ||
        '';

      const candidate = (email || name).trim();

      return candidate || 'guest';
    } catch {
      return 'guest';
    }
  }
  private isDuplicateName(name: string): boolean {
    const n = (name || '').trim().toLowerCase();
    const inList = this.simulations.some(sim => (sim.title || '').trim().toLowerCase() === n);
    const inStore = this.analysisStore.getRunsSnapshot().some(r => (r.title || '').trim().toLowerCase() === n);
    return inList || inStore;
  }
  private extractDupSignature(payload: any): string {
    const sig: any = {};
    const p = payload?.parameters || {};
    const htnGroups = [
      'Awareness raising', 'Screening corner', 'Active search', 'Diagnostic capacity UBS', 'Treatment capacity UBS',
      'Caring for all protocol', 'Remote training', 'Risk stratification tool', 'PAP card', 'Adherence combo',
      'Governance model', 'Management panel'
    ];
    sig.htn = {};
    htnGroups.forEach(g => { if (p[g] && typeof p[g] === 'object') sig.htn[g] = p[g]; });
    sig.cm = payload?.Costmetrics || {};
    const stableStringify = (obj: any): string => {
      if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
      if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`;
      const keys = Object.keys(obj).sort();
      return `{${keys.map(k => `"${k}":${stableStringify(obj[k])}`).join(',')}}`;
    };
    return stableStringify(sig);
  }
  private isDuplicateCoreParams(newPayload: any): boolean {
    const newSig = this.extractDupSignature(newPayload);
    return this.simulations.some(sim => {
      const req = sim.payload?.[0]?.request;
      if (!req) return false;
      const sig = this.extractDupSignature(req);
      return sig === newSig;
    });
  }

  private parseSimulationNumber(title: string | null | undefined): number | null {
    const t = (title || '').trim();
    const m = /^simulation\s+(\d+)$/i.exec(t);
    return m ? Number(m[1]) : null;
  }

  private collectUsedSimulationNumbers(): Set<number> {
    const used = new Set<number>();
    for (const s of this.simulations) {
      const n = this.parseSimulationNumber(s.title);
      if (n != null) used.add(n);
    }
    const runs = this.analysisStore.getRunsSnapshot();
    for (const r of runs) {
      const n = this.parseSimulationNumber(r.title);
      if (n != null) used.add(n);
    }
    return used;
  }

  private suggestSimulationName(): string {
    const used = this.collectUsedSimulationNumbers();
    let n = 2;
    while (used.has(n)) n++;
    return `Simulation ${n}`;
  }
}